import {
    useAddUserRole,
    useAddUserToGroup,
    useCreateUserAddress,
    useCreateUserCredit,
    useCreateUserLicense,
    useRemoveRoleAssignment,
} from "@Totum/Groups/State/mutations";
import { getRoleUserTag, TagType } from "@Totum/Main/Components/groups/TagProperties";
import useCurrentGroupId from "@Totum/Groups/State/hooks/useCurrentGroupId";
import { useGetUserCredits, useGetUserRoles } from "@Totum/Groups/State/queries";
import { GroupContext, SystemContext, UserContext } from "@Totum/Main/Constants";
import { useEffect, useState } from "react";
import { ActiveTabKey } from "@Totum/Groups/Constants";
import { IRoleAssignmentDetails } from "@Totum/Main/Domain/IRoleDetails";
import { ITag } from "@Totum/Main/Components/groups/GroupUtils";

/**
 * Orchestrates the operations for the `user assignment menu` component.
 */
const useUserAssignmentMenuOperations = (activeTabKey: string, groupUserIds?: string[]) => {
    // ** Constants & Variables ** //
    const [currentGroupId] = useCurrentGroupId();
    const [roleTags, setRoleTags] = useState<Record<string, ITag>>();

    // ** Hooks ** //

    const createUserAddressMutation = useCreateUserAddress(currentGroupId);
    const createUserLicenseMutation = useCreateUserLicense(currentGroupId);
    const createUserCreditMutation = useCreateUserCredit(currentGroupId);
    const addUserRoleMutation = useAddUserRole();
    const addUserToGroupMutation = useAddUserToGroup();
    const removeUserRoleAssignment = useRemoveRoleAssignment(currentGroupId, activeTabKey);
    const { data: roleAssignments } = useGetUserRoles(groupUserIds);
    const { data: userCredits, ...otherUserCreditsQueryProps } = useGetUserCredits(groupUserIds);

    // ** Functions ** //
    const removeRoleAssignment = (role: IRoleAssignmentDetails) => {
        removeUserRoleAssignment.mutateAsync(role);
    };

    /**
     * Perform mutations based upon which option is selected from the user assignment menu
     * @param type
     * @param params
     */
    const groupUserAddAction = (type: TagType, params: any) => {
        switch (type) {
            case TagType.AddressUser:
                createUserAddressMutation.mutateAsync(params);
                break;
            case TagType.LicenseUser:
                createUserLicenseMutation.mutateAsync(params);
                break;
            case TagType.CreditUser:
                createUserCreditMutation.mutateAsync(params);
                break;
            case TagType.Role:
                addUserRoleMutation.mutateAsync(params);
                break;
            case TagType.Assignment:
                addUserToGroupMutation.mutateAsync(params);
                break;
        }
    };

    /**
     * Perform mutations based upon which option is selected from the user assignment menu
     * @param type
     * @param params
     */
    const userSearchAddAction = (type: TagType, params: any) => {
        switch (type) {
            case TagType.AddressUser:
                createUserAddressMutation.mutateAsync(params);
                break;
            case TagType.LicenseUser:
                createUserLicenseMutation.mutateAsync(params);
                break;
            case TagType.CreditUser:
                createUserCreditMutation.mutateAsync(params);
                break;
            case TagType.Role:
                addUserRoleMutation.mutateAsync(params);
                break;
            case TagType.Assignment:
                addUserToGroupMutation.mutateAsync(params);
                break;
        }
    };

    /**
     * Rebuild role tags on demand
     */
    useEffect(() => {
        const tags: Record<string, ITag> = { ...roleTags };
        if (roleAssignments?.length === 0 && !!tags && Object.keys(tags)?.length === 0) return;
        roleAssignments?.forEach((role: IRoleAssignmentDetails) => {
            if (activeTabKey === ActiveTabKey.Groups && role.roleAssignmentId) {
                if (
                    role.contextId === SystemContext ||
                    role.contextId === UserContext ||
                    (role.contextId === GroupContext && role.instanceIds.map((x) => x.id).includes(currentGroupId))
                ) {
                    tags[role.roleAssignmentId] = getRoleUserTag(role.instanceIds?.map((x) => x.id)[0], role, () =>
                        removeRoleAssignment(role)
                    );
                }
            } else {
                if (role.contextId === SystemContext && role.roleAssignmentId) {
                    tags[role.roleAssignmentId] = getRoleUserTag(role.instanceIds?.map((x) => x.id)[0], role, () =>
                        removeRoleAssignment(role)
                    );
                }
            }
        });

        setRoleTags(tags);
    }, [roleAssignments]);

    // ** Exports ** //

    return {
        // data
        creditAssignments: userCredits,
        roleAssignments,
        roleTags,
        // state
        isFetchingUserCredits: otherUserCreditsQueryProps.isFetching,

        // methods
        groupUserAddAction,
        userSearchAddAction,
        removeRoleAssignment,
    };
};

export default useUserAssignmentMenuOperations;
